import { BarrierTriageStatus, Response, SceStatus } from "..";
import { ReasonKey, StepConclusionInfo } from "../../components";
import { Log, SelectOption } from "../generic";
import { ControlStatus, CtoStatus } from "../statusTypes";
import { TagStatusType } from "../tagTypes";
import { User } from "../userTypes";
import { Step1Response } from "./step1";
import { Step2Response } from "./step2";
import { Step3Response } from "./step3";
import { Step4Response } from "./step4";
import { Step5Response } from "./step5";
import { Step6Response } from "./step6";
import { Step7Response } from "./step7";
import { Step8Response } from "./step8";

export type CtoType = "COMMON" | "DEFERMENT";

export interface CtoDeferment {
  work_order_number: string;
  original_due_date: string;
}
export interface Cto extends Log {
  cto_id?: string | number;
  barrier_triage_status?: BarrierTriageStatus;
  cto_code: string;
  merge_status?: CtoMergeStatus;
  status: CtoStatus;
  current_step: number;
  creation_type: CreationTypeEnum;
  final_date?: Date | null;
  sces: SceStatus[];
  tags: TagStatusType[];
  versions: RejectedVersion[];
  manual_close: ManualClose | null;
  cto_summary: string | undefined;
  cto_title: string | null;
  tag_reason: ReasonKey[] | undefined;
  state_version?: number | null;
  are_linked_tags_healthy?: boolean;
  cto_type: CtoType;
  deferment: CtoDeferment | null;
}

/** Offer the CSG question for a common CTO whose linked tag is SCE-CSG.
 *
 * Identification GET currently sends `tag_type` but not `csg_codes`. Treat a
 * missing association list as eligible and let the evaluation POST validate
 * zero or many CSGs. When codes are present, require exactly one shared CSG.
 */
export const isCsgImpactEligible = (cto: Cto): boolean => {
  if (cto.cto_type !== "COMMON" || cto.deferment !== null) return false;
  const csgTags = cto.tags?.filter((tag) => tag.tag_type === "SCE-CSG") ?? [];
  if (csgTags.length === 0) return false;

  const knownCodes = csgTags
    .map((tag) => tag.csg_codes)
    .filter((codes): codes is string[] => codes !== undefined);
  if (knownCodes.length === 0) return true;

  return (
    knownCodes.length === csgTags.length &&
    knownCodes.every((codes) => codes.length === 1 && !!codes[0]) &&
    new Set(knownCodes.map((codes) => codes[0])).size === 1
  );
};

export interface CtoDashboard extends Log {
  cto_id?: string | number;
  cto_number: string;
  merge_status?: CtoMergeStatus;
  cto_title: string | null;
  status: CtoStatus;
  current_step: number;
  creation_type: CreationTypeEnum;
  final_date?: Date | null;
  linked_sces: SceStatus[];
  linked_tags: TagStatusType[];
  versions: RejectedVersion[];
  manual_close: ManualClose | null;
  summary: string | undefined;
  machine_version: 2 | 3;
  cto_type?: CtoType;
}

export interface RecommendationInfo {
  cto_status: string;
  cto_number: string;
  control_status: ControlStatus;
  recommendation: string;
  recommendation_user_name: string;
  due_date: Date;
}

export interface Recommendations {
  data: RecommendationInfo[];
  page: number | null;
  page_size: number;
  total: number;
}

export type ControlStatusCounts = Partial<Record<ControlStatus, number>>;

export type LiveControlStatusCount = { PENDING: number; IN_PROGRESS: number };

export type CtoStatusCounts = {
  count_by_status: Partial<Record<CtoStatus, number | LiveControlStatusCount>>;
};

export interface CtoGrid extends Log {
  cto_id: string | number;
  cto_code: string;
  merge_status: CtoMergeStatus;
  cto_title: string | null;
  status: CtoStatus;
  current_step: number;
  creation_type: CreationTypeEnum;
  finalDate: Date | string | undefined;
  cto_summary: string | undefined;
  sces: string[];
  tags: string[];
  machine_version: 2 | 3;
}

export interface StepProps<StepResponse> {
  cto: Cto;
  vesselId: string;
  ctoId: string;
  readOnly: boolean;
  ctoStepResponse: Response<StepResponse>;
  isCurrentVersion: boolean;
  shouldDispatchRecommendationEvent?: boolean;
  currentStep?: number;
  onConclusionDateLoaded?: (stepData: StepConclusionInfo) => void;
  reloadCto?: () => void;
}

export interface CtoForm<StepResponse> {
  step: CtoKeyStepEnum;
  FormContent: React.FC<StepProps<StepResponse>>;
}

export interface NewCtoForm<StepResponse> {
  step: NewCtoKeyStepEnum;
  FormContent: React.FC<StepProps<StepResponse>>;
}

export enum CreationTypeEnum {
  MANUAL = 0,
  AUTO = 1,
}

export type CtoMergeStatus = "MERGED" | "PENDING" | "DECLINED" | null;

export interface ExtendCtoBody {
  new_expiry_date: Date;
  justification: string;
  send_to_step_2: boolean;
}

export interface ExtendDueDateBody {
  due_date?: Date;
  temporary_due_date?: Date;
  justification: string;
}

export enum SceConditionEnum {
  SCE_IMPAIRED = 1,
  FACILITY_IS_OPERATING = 2,
  DURING_OTHER_ABNORMAL_SITUATIONS = 3,
  OTHER = 4,
}

//REFACTOR: passar para en.ts
const sceImpaired =
  "A Safety Critical Element (SCE) is impaired (e.g. critical failure of a SCE tagged item)";
const facilityIsOperating =
  "A facility, or part of a facility, is operating beyond its design conditions / procedures";
const duringOtherAbnormalSituations =
  "During other abnormal situations that may potentially increase Major Accident Event (MAE) risk levels.";
const other = "Other";

export const OptionsConditionFound: SelectOption[] = [
  {
    id: SceConditionEnum.SCE_IMPAIRED,
    label: sceImpaired,
  },
  {
    id: SceConditionEnum.FACILITY_IS_OPERATING,
    label: facilityIsOperating,
  },
  {
    id: SceConditionEnum.DURING_OTHER_ABNORMAL_SITUATIONS,
    label: duringOtherAbnormalSituations,
  },
  {
    id: SceConditionEnum.OTHER,
    label: other,
  },
];

export enum CtoStepEnum {
  STEP_1 = 1,
  STEP_2 = 2,
  STEP_3 = 3,
  STEP_4 = 4,
  STEP_5 = 5,
  STEP_6 = 6,
  STEP_7 = 7,
  STEP_8 = 8,
}

export enum CtoKeyStepEnum {
  STEP_1 = "initiation",
  STEP_2 = "evaluation",
  STEP_3 = "review",
  STEP_4 = "approval",
  STEP_5 = "recommendations",
  STEP_6 = "reassessment",
  STEP_7 = "verification",
  STEP_8 = "closure",
}

export enum NewCtoKeyStepEnum {
  STEP_1 = "initiation",
  STEP_2 = "evaluation",
  STEP_3 = "review",
  STEP_4 = "recommendation",
  STEP_5 = "reassessment",
  STEP_6 = "closure",
}

export enum CloseAlertStatus {
  CTO_IS_NO_NECESSARY,
  CTO_IN_STEP8,
}

export interface CtoSummary {
  CLOSED: number;
  COMPLETED: number;
  EXPIRED: number;
  EXTENSION_REQUIRED: number;
  LIVE: number;
  UNDER_EVALUATION: number;
  vessel_code: string;
}

export interface CTOsPerTagListResponse {
  ctos: string[];
  tagCode: string;
}

export interface RejectedVersion {
  rejected_by: User;
  rejected_from_step: number;
  rejected_to_step: number;
  version: number;
  version_date: string;
}

export interface ManualClose {
  closed_at: Date;
  closed_by: User;
  reason: string;
}

export type AnyStepResponse = Step1Response &
  Step2Response &
  Step3Response &
  Step4Response &
  Step5Response &
  Step6Response &
  Step7Response &
  Step8Response;

export type DateHistoryItem = {
  date_time: string;
  new_value: string;
  previous_value: string;
  username: string;
  justification: string | null;
};

export type CtoExpiryDateHistory = {
  cto_code: string;
  current_due_date: string;
  history: DateHistoryItem[];
};
